import type { CatalogItem } from '@viva/core';
import { useEffect, useState } from 'react';
import { useOptionalAccount } from '../../account/AccountGate';
import { t } from '../../i18n';

/** Playing this long before the viewer is asked (Abel: 5 seconds). */
export const ASK_AFTER_MS = 5_000;
/** The question goes away by itself after this. */
const ASK_FOR_MS = 60_000;

/** Channels already asked about in this app session (answered or dismissed): never asked twice. */
const asked = new Set<string>();

/**
 * "Is Everton vs Newcastle on this channel? Yes / No" (#156): for a channel opened from a
 * match that is only a guess (one of a brand's numbered feeds, Sky Sports+ 1, 2, 3…). Once
 * enough viewers agree, the answer applies to everyone.
 */
export function MatchQuestion({ item, playing }: { item: CatalogItem; playing: boolean }) {
  const account = useOptionalAccount();
  const match = item.data.sportsMatch;
  const streamId = item.data.streamId;
  const key = match && streamId !== undefined ? `${match.id}:${streamId}` : null;
  const [state, setState] = useState<'idle' | 'asking' | 'thanks'>('idle');

  useEffect(() => {
    setState('idle');
  }, [key]);

  // Ask once the channel has played for a while (and only if the account server wants an answer).
  useEffect(() => {
    if (!account || !match || streamId === undefined || !key || !playing || state !== 'idle' || asked.has(key)) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      void account.matchQuestion(match.id, streamId).then((ask) => {
        if (cancelled || !ask) return;
        asked.add(key);
        setState('asking');
      });
    }, ASK_AFTER_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [account, match, streamId, key, playing, state]);

  useEffect(() => {
    if (state === 'idle') return undefined;
    const timer = setTimeout(() => setState('idle'), state === 'thanks' ? 3000 : ASK_FOR_MS);
    return () => clearTimeout(timer);
  }, [state]);

  if (!account || !match || streamId === undefined || state === 'idle') return null;
  if (state === 'thanks') {
    return (
      <div className="player__toast" role="status">
        {t('player.matchThanks')}
      </div>
    );
  }
  const answer = (yes: boolean) => {
    setState('thanks');
    void account.answerMatchQuestion(match.id, streamId, yes);
  };
  return (
    <div
      className="player__notice player__question"
      role="dialog"
      aria-label={t('player.matchQuestion', { match: match.title })}
    >
      <span>{t('player.matchQuestion', { match: match.title })}</span>
      <button type="button" className="button primary" onClick={() => answer(true)}>
        {t('player.matchYes')}
      </button>
      <button type="button" className="button" onClick={() => answer(false)}>
        {t('player.matchNo')}
      </button>
      <button type="button" className="button ghost" onClick={() => setState('idle')} aria-label={t('common.close')}>
        ✕
      </button>
    </div>
  );
}
